/**
 * The console's pages: a sector's (under `/:sector/…`) and the platform's
 * (under `/platform/…`). One list for the routes, the sidebar and the switch.
 */
import type { ElementType } from 'react'
import {
  ArchiveBoxXMarkIcon,
  BanknotesIcon,
  BellIcon,
  HeartIcon,
  BookOpenIcon,
  BuildingOffice2Icon,
  CheckBadgeIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  EyeIcon,
  HomeIcon,
  LifebuoyIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  TagIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline'

export interface SectorPage {
  /** Below `/:sector`; '' is the sector's Home. */
  path: string
  name: string
  icon: ElementType
  group: string | null
  /** Only these sectors have the page (Education's curriculum). */
  sectors?: string[]
}

export const SECTOR_PAGES: SectorPage[] = [
  { path: '', name: 'Home', icon: HomeIcon, group: null },
  { path: 'customers', name: 'Customers', icon: BuildingOffice2Icon, group: 'Customers' },
  { path: 'onboarding', name: 'Onboarding', icon: ClipboardDocumentListIcon, group: 'Customers' },
  { path: 'verifications', name: 'Verifications', icon: CheckBadgeIcon, group: 'Customers' },
  { path: 'support', name: 'Support', icon: LifebuoyIcon, group: 'Customers' },
  { path: 'offboarding', name: 'Offboarding', icon: ArchiveBoxXMarkIcon, group: 'Customers' },
  { path: 'curriculum-review', name: 'Curriculum review', icon: BookOpenIcon, group: 'Content', sectors: ['school'] },
  { path: 'curriculum-library', name: 'Curriculum library', icon: EyeIcon, group: 'Content', sectors: ['school'] },
  { path: 'billing-health', name: 'Billing health', icon: HeartIcon, group: 'Billing' },
  { path: 'prices', name: 'Prices', icon: BanknotesIcon, group: 'Billing' },
  { path: 'discounts', name: 'Discounts', icon: TagIcon, group: 'Billing' },
  { path: 'billing-approvals', name: 'Approvals', icon: CheckBadgeIcon, group: 'Billing' },
  { path: 'payment-connections', name: 'Payment connections', icon: CreditCardIcon, group: 'Billing' },
  { path: 'activity', name: 'Activity log', icon: BellIcon, group: 'Records' },
]

export interface PlatformPage {
  /** Below `/platform`. */
  path: string
  name: string
  icon: ElementType
}

export const PLATFORM_PAGES: PlatformPage[] = [
  { path: 'sectors', name: 'Sectors', icon: Squares2X2Icon },
  { path: 'roles', name: 'Roles & permissions', icon: ShieldCheckIcon },
  { path: 'activity', name: 'Platform activity', icon: BellIcon },
  { path: 'account', name: 'Your account', icon: UserCircleIcon },
]

/** Old addresses (before sector workspaces) and where they live now. */
export const MOVED_TO_SECTOR: Record<string, string> = {
  tenants: 'customers',
  onboarding: 'onboarding',
  verifications: 'verifications',
  support: 'support',
  'curriculum-qa': 'curriculum-review',
  'curriculum-viewer': 'curriculum-library',
  prices: 'prices',
  discounts: 'discounts',
  'billing-approvals': 'billing-approvals',
  integrations: 'payment-connections',
}

export const MOVED_TO_PLATFORM: Record<string, string> = {
  verticals: 'sectors',
  roles: 'roles',
  'audit-logs': 'activity',
  settings: 'account',
}
